import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    DataApproverAccessResponse,
    DataApproverAccessSearchResponse,
    DataApproverRequest,
    DataApproverUsersSearchResponse,
    DataApproverVesselGroupsSearchResponse,
    UpsertDataApproverAccessRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreDataApprover.api.ts';
import {
    createApiDataApproverAccess,
    deleteApiDataApproverAccess,
    getApiDataApproverAccess,
    getApiDataApproverUsers,
    getApiDataApproverVesselGroups,
    updateApiDataApproverAccess,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreDataApprover.api.ts';
import { extractError } from '@utils/slice.ts';

interface CrewDataApproverError {
    message?: string;
    [key: string]: any;
}

interface CrewDataApproverState {
    error: CrewDataApproverError | null;
    crewDataApproversData: DataApproverAccessSearchResponse | null;
    crewDataApproverUsersData: DataApproverUsersSearchResponse | null;
    crewDataApproverVesselGroupsData: DataApproverVesselGroupsSearchResponse | null;

    isGetCrewDataApproversPending: boolean;
    isCreateCrewDataApproverPending: boolean;
    isUpdateCrewDataApproverPending: boolean;
    isDeleteCrewDataApproverPending: boolean;
    isGetCrewDataApproverUsersPending: boolean;
    isGetCrewDataApproverVesselGroupsPending: boolean;
}

const initialState: CrewDataApproverState = {
    error: null,
    crewDataApproversData: null,
    crewDataApproverUsersData: null,
    crewDataApproverVesselGroupsData: null,

    isGetCrewDataApproversPending: false,
    isCreateCrewDataApproverPending: false,
    isUpdateCrewDataApproverPending: false,
    isDeleteCrewDataApproverPending: false,
    isGetCrewDataApproverUsersPending: false,
    isGetCrewDataApproverVesselGroupsPending: false,
};

export const getCrewDataApprovers = createAsyncThunk<
    DataApproverAccessSearchResponse,
    { tenantAlias: string; payload: DataApproverRequest },
    { rejectValue: CrewDataApproverError }
>('crewDataApprover/getCrewDataApprovers', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataApproverAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createCrewDataApprover = createAsyncThunk<
    DataApproverAccessResponse,
    { tenantAlias: string; payload: UpsertDataApproverAccessRequest },
    { rejectValue: CrewDataApproverError }
>('crewDataApprover/createCrewDataApprover', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiDataApproverAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateCrewDataApprover = createAsyncThunk<
    DataApproverAccessResponse,
    { tenantAlias: string; accessId: number; payload: UpsertDataApproverAccessRequest },
    { rejectValue: CrewDataApproverError }
>('crewDataApprover/updateCrewDataApprover', ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
    updateApiDataApproverAccess(tenantAlias, accessId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteCrewDataApprover = createAsyncThunk<
    void,
    { tenantAlias: string; accessId: number },
    { rejectValue: CrewDataApproverError }
>('crewDataApprover/deleteCrewDataApprover', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiDataApproverAccess(tenantAlias, accessId)
        .then(() => undefined)
        .catch((err) => rejectWithValue(err)),
);

export const getCrewDataApproverUsers = createAsyncThunk<
    DataApproverUsersSearchResponse,
    { tenantAlias: string; payload: DataApproverRequest },
    { rejectValue: CrewDataApproverError }
>('crewDataApprover/getCrewDataApproverUsers', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataApproverUsers(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getCrewDataApproverVesselGroups = createAsyncThunk<
    DataApproverVesselGroupsSearchResponse,
    { tenantAlias: string; payload: DataApproverRequest },
    { rejectValue: CrewDataApproverError }
>('crewDataApprover/getCrewDataApproverVesselGroups', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataApproverVesselGroups(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const crewDataApproverSlice = createSlice({
    name: 'crewDataApprover',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getCrewDataApprovers.pending, (state) => {
            state.isGetCrewDataApproversPending = true;
            state.error = null;
        });
        builder.addCase(getCrewDataApprovers.fulfilled, (state, action) => {
            state.isGetCrewDataApproversPending = false;
            state.crewDataApproversData = action.payload;
        });
        builder.addCase(getCrewDataApprovers.rejected, (state, action) => {
            state.isGetCrewDataApproversPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createCrewDataApprover.pending, (state) => {
            state.isCreateCrewDataApproverPending = true;
            state.error = null;
        });
        builder.addCase(createCrewDataApprover.fulfilled, (state) => {
            state.isCreateCrewDataApproverPending = false;
        });
        builder.addCase(createCrewDataApprover.rejected, (state, action) => {
            state.isCreateCrewDataApproverPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateCrewDataApprover.pending, (state) => {
            state.isUpdateCrewDataApproverPending = true;
            state.error = null;
        });
        builder.addCase(updateCrewDataApprover.fulfilled, (state) => {
            state.isUpdateCrewDataApproverPending = false;
        });
        builder.addCase(updateCrewDataApprover.rejected, (state, action) => {
            state.isUpdateCrewDataApproverPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteCrewDataApprover.pending, (state) => {
            state.isDeleteCrewDataApproverPending = true;
            state.error = null;
        });
        builder.addCase(deleteCrewDataApprover.fulfilled, (state) => {
            state.isDeleteCrewDataApproverPending = false;
        });
        builder.addCase(deleteCrewDataApprover.rejected, (state, action) => {
            state.isDeleteCrewDataApproverPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getCrewDataApproverUsers.pending, (state) => {
            state.isGetCrewDataApproverUsersPending = true;
            state.error = null;
        });
        builder.addCase(getCrewDataApproverUsers.fulfilled, (state, action) => {
            state.isGetCrewDataApproverUsersPending = false;
            state.crewDataApproverUsersData = action.payload;
        });
        builder.addCase(getCrewDataApproverUsers.rejected, (state, action) => {
            state.isGetCrewDataApproverUsersPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getCrewDataApproverVesselGroups.pending, (state) => {
            state.isGetCrewDataApproverVesselGroupsPending = true;
            state.error = null;
        });
        builder.addCase(getCrewDataApproverVesselGroups.fulfilled, (state, action) => {
            state.isGetCrewDataApproverVesselGroupsPending = false;
            state.crewDataApproverVesselGroupsData = action.payload;
        });
        builder.addCase(getCrewDataApproverVesselGroups.rejected, (state, action) => {
            state.isGetCrewDataApproverVesselGroupsPending = false;
            state.error = extractError(action);
        });
    },
});

export default crewDataApproverSlice.reducer;
